import { Box, FormLabel, Stack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import {
  FIXED_GEMINI_FLASH,
  fallbackCatalog,
  findGroupedOption,
  loadGraderModelCatalog,
  rememberGradingModel,
  rememberOcrModel,
  rememberedGradingModelId,
  rememberedOcrModelId,
  resolveGradingModel,
  resolveOcrModel,
  toGradingModelGroups,
  toOcrModelGroups,
} from "../../../modules/admin-modules/mark-schemes/grader-models";

const selectStyles = {
  ...adminSelectStyles(),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  groupHeading: (base) => ({
    ...base,
    color: "var(--text-muted)",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  }),
};

export function useGraderModelSelection(active) {
  const [ocrGroups, setOcrGroups] = useState(() => toOcrModelGroups(fallbackCatalog()));
  const [gradingGroups, setGradingGroups] = useState(() => toGradingModelGroups(fallbackCatalog()));
  const [ocrModel, setOcrModel] = useState(() => rememberedOcrModelId() || FIXED_GEMINI_FLASH);
  const [gradingModel, setGradingModel] = useState(
    () => rememberedGradingModelId() || FIXED_GEMINI_FLASH
  );
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setIsLoadingModels(true);
    loadGraderModelCatalog()
      .then((catalog) => {
        if (cancelled) return;
        const nextOcrGroups = toOcrModelGroups(catalog);
        const nextGradingGroups = toGradingModelGroups(catalog);
        const nextOcr = resolveOcrModel(catalog, nextOcrGroups, rememberedOcrModelId());
        const nextGrading = resolveGradingModel(
          catalog,
          nextGradingGroups,
          rememberedGradingModelId()
        );
        setOcrGroups(nextOcrGroups);
        setGradingGroups(nextGradingGroups);
        setOcrModel(nextOcr);
        setGradingModel(nextGrading);
        rememberOcrModel(nextOcr);
        rememberGradingModel(nextGrading);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingModels(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  const onOcrModelChange = (modelId) => {
    rememberOcrModel(modelId);
    setOcrModel(modelId);
  };

  const onGradingModelChange = (modelId) => {
    rememberGradingModel(modelId);
    setGradingModel(modelId);
  };

  return {
    ocrGroups,
    gradingGroups,
    ocrModel,
    gradingModel,
    onOcrModelChange,
    onGradingModelChange,
    isLoadingModels,
  };
}

const ModelSelect = ({ label, groups, value, onChange, isDisabled, isLoading, placeholder }) => (
  <Box>
    <FormLabel fontSize="xs" fontWeight="700" mb={1}>
      {label}
    </FormLabel>
    <Select
      styles={selectStyles}
      options={groups}
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      value={findGroupedOption(groups, value)}
      onChange={(option) => onChange(option?.value ?? FIXED_GEMINI_FLASH)}
      placeholder={placeholder}
      isLoading={isLoading}
      isDisabled={isDisabled || isLoading}
      isClearable={false}
    />
  </Box>
);

export const OcrModelField = ({ groups, value, onChange, isDisabled, isLoading }) => (
  <ModelSelect
    label="OCR model"
    groups={groups}
    value={value}
    onChange={onChange}
    isDisabled={isDisabled}
    isLoading={isLoading}
    placeholder="Select OCR model"
  />
);

const GradingModelField = ({ groups, value, onChange, isDisabled, isLoading }) => (
  <ModelSelect
    label="Grading model"
    groups={groups}
    value={value}
    onChange={onChange}
    isDisabled={isDisabled}
    isLoading={isLoading}
    placeholder="Select grading model"
  />
);

export const GraderModelFields = ({
  ocrGroups,
  gradingGroups,
  ocrModel,
  gradingModel,
  onOcrModelChange,
  onGradingModelChange,
  isDisabled,
  isLoading,
}) => (
  <Stack spacing={4}>
    <OcrModelField
      groups={ocrGroups}
      value={ocrModel}
      onChange={onOcrModelChange}
      isDisabled={isDisabled}
      isLoading={isLoading}
    />
    <GradingModelField
      groups={gradingGroups}
      value={gradingModel}
      onChange={onGradingModelChange}
      isDisabled={isDisabled}
      isLoading={isLoading}
    />
  </Stack>
);

export default GradingModelField;
